import React from 'react';
import { ListItem, ListItemText, Collapse, Typography, ListItemButton, ListItemIcon, Box } from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { List } from '@mui/material';
import { MenuItem } from './menuConfig';
import { ShowForPermission } from './permissions/ShowForPermission';
import { ShowForRole } from './permissions/ShowForRole';
import { permissionService } from '../../services/permissionService';

// Константы для анимации аккордеона
const ACCORDION_ANIMATION_DURATION = {
    enter: 220,
    exit: 180,
};

interface MenuItemComponentProps {
    item: MenuItem;
    currentIndex: string;
    accordionOpen: { [key: string]: boolean };
    closingIndex: string | null;
    onAccordionToggle: (index: string, isCurrentlyOpen: boolean) => void;
    onNavigate: (link: string) => void;
    onLogout: () => void;
    renderChildren: (items: MenuItem[], parentIndex: string) => React.ReactNode;
}

export const MenuItemComponent: React.FC<MenuItemComponentProps> = ({
    item,
    currentIndex,
    accordionOpen,
    closingIndex,
    onAccordionToggle,
    onNavigate,
    onLogout,
    renderChildren
}) => {
    const hasChildren = Boolean(item.children && item.children.length > 0);
    // Аккордеон открыт, если явно открыт через accordionOpen (для анимации) или если элемент активен
    // Приоритет ВСЕГДА отдаем accordionOpen для правильной работы анимации при ручном открытии
    // Если аккордеон закрывается, он не должен быть открыт
    const isAccordionOpen = closingIndex === currentIndex
        ? false // Если аккордеон закрывается, он закрыт
        : (accordionOpen.hasOwnProperty(currentIndex)
            ? accordionOpen[currentIndex]
            : Boolean(item.isActive));
    // Элемент выделяется, если он активен по пути, или если аккордеон открыт вручную
    const isHighlighted = (item.isActive && item.isPrimary) || 
                          (hasChildren && item.isPrimary && accordionOpen[currentIndex] && !item.isActive);
    const MyIcon = item.icon;

    const handleClick = () => {
        if (item.isDisabled) return;

        if (item.link === '/logout') {
            onLogout();
        } else if (hasChildren && item.isPrimary) {
            // Передаем текущее состояние аккордеона (учитываем и явное открытие, и активное состояние)
            const isCurrentlyOpen = accordionOpen.hasOwnProperty(currentIndex)
                ? accordionOpen[currentIndex]
                : (item.isActive || false);
            
            // Сначала переключаем аккордеон
            onAccordionToggle(currentIndex, isCurrentlyOpen);
            
            // Если аккордеон открывается и есть первый дочерний элемент с link, навигируем на него
            // Задержка должна учитывать время закрытия предыдущего аккордеона + время открытия нового
            if (!isCurrentlyOpen && item.children && item.children.length > 0) {
                const firstChildWithLink = item.children.find(child =>
                    child.link &&
                    !child.isDisabled &&
                    (!child.requiredRole || permissionService.hasRole(child.requiredRole)) &&
                    (!child.requiredPermission || permissionService.canDo(
                        child.requiredPermission.catalog,
                        child.requiredPermission.permission
                    ))
                );
                const linkToNavigate = firstChildWithLink?.link;
                if (linkToNavigate) {
                    // Навигация происходит после завершения анимации открытия
                    // При одновременном закрытии/открытии используем время открытия
                    setTimeout(() => {
                        onNavigate(linkToNavigate);
                    }, ACCORDION_ANIMATION_DURATION.enter);
                }
            }
        } else if (item.link) {
            onNavigate(item.link);
        }
    };

    const menuItemContent = (
        <>
            <ListItem disablePadding sx={{ mt: item.isPrimary ? 0.5 : 0.25, display: 'block' }}>
                <ListItemButton
                    onClick={handleClick}
                    disabled={item.isDisabled}
                    selected={Boolean(item.isActive)}
                    sx={{
                        minHeight: item.isPrimary ? 44 : 38,
                        px: item.isPrimary ? 1.5 : 1.25,
                        pl: item.isPrimary ? 1.5 : 5.5,
                        borderRadius: 1,
                        color: isHighlighted || item.isActive ? 'primary.dark' : 'text.primary',
                        bgcolor: isHighlighted ? 'primary.main' : 'transparent',
                        '&.Mui-selected': { bgcolor: item.isPrimary ? 'primary.main' : 'primary.light' },
                        '&.Mui-selected:hover': { bgcolor: item.isPrimary ? 'primary.dark' : 'primary.light' },
                        '&:hover': { bgcolor: isHighlighted ? 'primary.dark' : 'rgba(149, 136, 232, 0.1)' },
                    }}
                >
                    <ListItemIcon sx={{ minWidth: item.isPrimary ? 34 : 0 }}>
                        <Box sx={{ display: 'flex', color: isHighlighted ? 'common.white' : 'text.secondary', minWidth: 24 }}>
                            {MyIcon ? <MyIcon stroke={isHighlighted ? "white" : "#68697a"} /> : null}
                        </Box>
                    </ListItemIcon>
                    <ListItemText
                        primary={<Typography fontSize={14} fontWeight={item.isPrimary ? 700 : 500} color={isHighlighted ? 'common.white' : 'inherit'}>{item.text}</Typography>}
                    />
                    {hasChildren && item.isPrimary && (
                        <Box sx={{ color: isHighlighted ? 'common.white' : 'text.secondary', display: 'flex' }}>
                            {isAccordionOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </Box>
                    )}
                </ListItemButton>
            </ListItem>
            {hasChildren && (
                <Collapse 
                    key={`collapse-${currentIndex}`}
                    in={isAccordionOpen} 
                    timeout={ACCORDION_ANIMATION_DURATION}
                    unmountOnExit={false}
                    sx={{
                        '& .MuiCollapse-wrapper': {
                            transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
                            overflow: 'hidden', // Скрываем overflow во время анимации
                        },
                        '& .MuiCollapse-wrapperInner': {
                            overflow: 'hidden', // Скрываем overflow во время анимации
                        },
                        '& .MuiCollapse-root': {
                            overflow: 'hidden', // Скрываем overflow во время анимации
                        },
                    }}
                >
                    <List 
                        component="div" 
                        disablePadding
                        sx={{
                            overflow: 'hidden', // Скрываем overflow, чтобы предотвратить появление полос прокрутки
                        }}
                    >
                        {renderChildren(item.children ?? [], `${currentIndex}-`)}
                    </List>
                </Collapse>
            )}
        </>
    );

    // Оборачиваем в компоненты проверки прав доступа при необходимости
    if (item.requiredRole) {
        return (
            <ShowForRole key={currentIndex} role={item.requiredRole}>
                {menuItemContent}
            </ShowForRole>
        );
    }

    if (item.requiredPermission) {
        return (
            <ShowForPermission
                key={currentIndex}
                catalog={item.requiredPermission.catalog}
                permission={item.requiredPermission.permission}
            >
                {menuItemContent}
            </ShowForPermission>
        );
    }

    return <React.Fragment key={currentIndex}>{menuItemContent}</React.Fragment>;
};
